import { Skeleton } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { activeProviderStats } from '#lib/react-query/queries.ts';
import { STAT_WIDGET_CHART_HEIGHT } from '#utils/charts.ts';
import { Base } from '../Base.tsx';
import {
  InfoEmphasis,
  InfoList,
  InfoParagraph,
  InfoSection,
  InfoTemplate,
} from '../InfoTemplateComponents.tsx';
import { useAppointmentWidget } from '../use-appointment-widget.ts';
import { StatBase } from './StatBase.tsx';

const widgetTitle = 'Active Providers';

const infoTemplate = (
  <InfoTemplate>
    <InfoSection title="What it is &amp; Problems it Solves">
      <InfoParagraph>
        Counts the number of&nbsp;
        <InfoEmphasis>unique providers</InfoEmphasis>
        &nbsp;who delivered care during the selected time period. This metric helps track clinical
        capacity, monitor staff utilization, and assess operational coverage.
      </InfoParagraph>
    </InfoSection>

    <InfoSection title="How it Works (Calculation)" sx={{ mb: 2 }}>
      <InfoSection title="Active Providers =" indented>
        <InfoList
          items={[
            'Count of unique providers',
            'From appointments that are not deleted, canceled, no-showed',
            'Where provider information is available (not blank)',
            'Respects all applied filters (date, location, etc.)',
          ]}
        />
      </InfoSection>

      <InfoSection title="Includes" indented>
        <InfoList
          items={[
            'Appointments with a valid provider Id',
            'Appointments that start within the selected date range and filters',
          ]}
        />
      </InfoSection>

      <InfoSection title="Excludes" indented>
        <InfoList
          items={['Deleted appointments', 'Canceled appointments', 'No-showed appointments']}
        />
      </InfoSection>

      <InfoSection title="Notes &amp; Caveats" indented>
        <InfoList
          items={[
            <>
              A provider is counted&nbsp;
              <InfoEmphasis>once</InfoEmphasis>
              &nbsp;per date range, even if they delivered care in multiple appointments
            </>,
            'This stat card includes all non-deleted appointments with assigned providers, regardless of whether the appointment was completed, canceled, or a no-show',
            'Data updates nightly, so very recent changes may not yet appear.',
          ]}
        />
      </InfoSection>
    </InfoSection>
  </InfoTemplate>
);

export function ActiveProvidersRate() {
  const widget = useAppointmentWidget();

  const { data, isLoading } = useQuery(activeProviderStats(widget.getCommonRequestParams()));

  if (isLoading) {
    return (
      <Base title={widgetTitle}>
        <Skeleton variant="rounded" height={STAT_WIDGET_CHART_HEIGHT} />
      </Base>
    );
  }

  return (
    <StatBase
      id="stat-widget-active-providers"
      data={{
        id: 'active-providers',
        title: widgetTitle,
        value: data?.value ?? null,
        valueType: 'absolute',
        trend:
          data && data.value !== 0 && data.trend
            ? {
                ...data.trend,
                daysInPeriod: widget.homeLoaderData.totalDaysInRange,
              }
            : undefined,
      }}
      infoTemplate={infoTemplate}
      drilldownRoutePath={widget.getDrilldownRoutePath()}
    />
  );
}
